import * as React from 'react';
// @ts-expect-error
import {components} from 'react-select';
// @ts-expect-error
import type {OptionProps, SingleValueProps} from 'react-select';

import {useMutation} from '@apollo/react-hooks';

import {OptionWithCount} from 'shells/select-option-components/option-with-count';
import type {ListOptionWithCount} from 'shells/select-option-components/option-with-count';

import type {
    GetAudiencesBasic as GetAudiencesQuery,
    SelectCreateAudience as SelectCreateAudienceMutation,
    SelectCreateAudienceVariables as SelectCreateAudienceMutationVariables,
} from 'nutshell-graphql-types';

import {useGetSessionUser} from '.././use-get-session-user';

import {GraphQLSelect} from './graphql-select';
import CREATE_AUDIENCE from './mutations/create-audience.graphql';
import GET_AUDIENCES from './queries/get-audiences.graphql';

type Props = {
    selectedId: string | null | undefined;
    options: ListOptionWithCount[];
    onChange: (newValue: ListOptionWithCount) => any;
    refetch?: () => Promise<any>;
    updateOptions?: (newOption: ListOptionWithCount) => void;
    onClearValue?: () => void;
    labelKey?: string;
    valueKey?: string;
    autoFocus?: boolean;
    autoBlur?: boolean;
    isLoading?: boolean;
    noResultsText?: string;
    styles?: any;
    menuPortalTarget?: HTMLElement;
    defaultsToFirstItem?: boolean;
    isCreateable?: boolean;
    hasError?: boolean | null | undefined;
    isDisabled?: boolean;
};

export const GraphQLSelectAudiences = (props: Props) => {
    const [createAudience] = useMutation<
        SelectCreateAudienceMutation,
        SelectCreateAudienceMutationVariables
    >(CREATE_AUDIENCE);

    const {user} = useGetSessionUser();

    return (
        <GraphQLSelect
            {...props}
            onChange={(newValue: ListOptionWithCount) => {
                if (!props.options.find((option) => option.value === newValue.value)) {
                    const tempNewOption = {
                        value: newValue.value,
                        label: newValue.label,
                        count: 0,
                    } as const;
                    props.updateOptions?.(tempNewOption);

                    // This is a created value, need to run our mutation.
                    createAudience({
                        variables: {
                            audience: {audience: {name: newValue.label}},
                        },
                        update: (proxy, mutationResults) => {
                            // Read the data from our cache for this query.
                            const cacheResult: GetAudiencesQuery | null | undefined =
                                proxy.readQuery({
                                    query: GET_AUDIENCES,
                                });

                            if (!cacheResult || !cacheResult.audiences) {
                                return;
                            }

                            const newAudience =
                                mutationResults &&
                                mutationResults.data &&
                                mutationResults.data.audienceCreate &&
                                mutationResults.data.audienceCreate.audience;

                            if (!newAudience) return;

                            proxy.writeQuery({
                                query: GET_AUDIENCES,
                                data: {
                                    audiences: {
                                        ...cacheResult.audiences,
                                        edges: [
                                            {
                                                cursor: newAudience.id,
                                                node: newAudience,
                                                __typename: 'AudiencesEdge',
                                            },
                                            // @ts-expect-error
                                        ].concat(cacheResult.audiences.edges),
                                    },
                                },
                            });

                            const newAudienceValue = {
                                value: newAudience.id,
                                label: newAudience.name,
                                count: newAudience.membershipStats.totalCount.value,
                            } as const;

                            // Update our selected item to the new item
                            props.onChange(newAudienceValue);

                            // Update the audience options to include the new one immediately
                            props.updateOptions?.(newAudienceValue);

                            // Refetch the audience options from the server
                            props.refetch?.();
                        },
                    });
                } else {
                    return props.onChange(newValue);
                }
            }}
            isLoading={props.isLoading}
            // @ts-expect-error
            openMenuOnFocus={true}
            noResultsText='No audiences were found'
            placeholder={props.isCreateable ? 'Select or create an audience' : 'Select an audience'}
            valueKey='value'
            labelKey='label'
            components={{
                Option: OptionComponent,
                SingleValue: Value,
            }}
            models={props.options}
            hasError={props.hasError}
            footerButton={
                props.isCreateable && user && user.permissions.canAccessMarketing
                    ? {
                          label: 'Manage audiences',
                          onClick: () => {
                              window.open('/email-marketing/audiences', '_blank');
                          },
                      }
                    : undefined
            }
            isDisabled={props.isDisabled}
        />
    );
};

const Value = (props: SingleValueProps) => {
    const spreadableProps: OptionProps = props as any;
    const contactDescriptor = props.data.count === 1 ? 'person' : 'people';
    const title = `${props.data.count} ${contactDescriptor} in this audience`;

    return (
        <components.SingleValue {...spreadableProps}>
            <OptionWithCount
                title={title}
                count={props.data.count}
                label={props.data.label}
                isFx={props.data.isFx}
                isSyncing={props.data.isSyncing}
            />
        </components.SingleValue>
    );
};

const OptionComponent = (props: OptionProps) => {
    // This is a hack to get around limitations in our old version of flow
    const spreadableProps: OptionProps = props as any;
    const contactDescriptor = props.data.count === 1 ? 'person' : 'people';
    const title = `${props.data.count} ${contactDescriptor} in this audience`;

    return (
        <components.Option {...spreadableProps}>
            <OptionWithCount
                title={title}
                count={props.data.count}
                label={props.data.label}
                isFx={props.data.isFx}
                isSyncing={props.data.isSyncing}
            />
        </components.Option>
    );
};
